import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { take } from 'rxjs';

import { UserInteractionService } from '@usheru-hff/community-elements/utils';
import { getMovieGenres, getMovieTags, List, Movie, User } from '@usheru-hff/shared/domain';
import { TrackingService } from '@usheru-hff/sdk/tracking';

import { UiCreateListFormComponent } from './ui-create-list-form/ui-create-list-form.component';

@Component({
  selector: 'community-elements-movie-list-handler',
  template: `
    <!-- <ush-search-list (listsLoaded)="onListsLoaded($event)"></ush-search-list> -->
    <!-- search box -->
    <ng-container *ngIf="!addListFormVisible">
      <div class="subtitle">{{ description }}</div>
      <div class="autocomplete-wrapper">
        <i class="pi pi-search"></i>
        <p-autoComplete
          [suggestions]="lists"
          [minLength]="0"
          field="name"
          dropdownIcon="pi pi-search"
          (completeMethod)="search($event)"
        >
          <!-- empty loading icon -->
          <ng-template pTemplate="loadingicon"></ng-template>
        </p-autoComplete>
      </div>

      <!-- found Lists -->
      <div class="lists-container">
        <div class="list new-list" (click)="onCreateListClick()">
          <div class="list-name">
            <i class="pi pi-plus"></i><span i18n="@@movie-list-handler.newList">New List</span>
          </div>
        </div>
        <ng-container *ngIf="loadingLists; else listsTemplate">
          <div class="loading">
            <i class="pi pi-spin pi-spinner"></i>
          </div>
        </ng-container>
      </div>

      <button
        pButton
        [disabled]="!selectedList"
        [loading]="waitingForResponse"
        label="Add to list"
        i18n-label="@@movie-list-handler.addToList"
        (click)="onAddMovieToListClick(selectedList)"
      ></button>
    </ng-container>

    <community-elements-ui-create-list-form
      *ngIf="addListFormVisible"
      (listCreated)="onListCreated($event)"
    ></community-elements-ui-create-list-form>

    <ng-template #listsTemplate>
      <div
        class="list"
        *ngFor="let list of foundLists"
        (click)="onListClick(list)"
        [ngClass]="{ selected: selectedList === list }"
      >
        <div class="list-details">
          <i *ngIf="list === selectedList" class="pi pi-check"></i>
          <div>
            <div class="list-name">{{ list.name }}</div>
            <div class="list-description">{{ (list.movies?.length || 0) + ' films' }}</div>
          </div>
        </div>
        <i *ngIf="!list.visible" class="pi pi-lock"></i>
      </div>
    </ng-template>
  `,
  styleUrls: ['./movie-list-handler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, AutoCompleteModule, ButtonModule, UiCreateListFormComponent]
})

/**
 * @description Renders a list of lists to add a movie to
 */
export class MovieListHandlerComponent implements OnInit {
  @Output() complete = new EventEmitter<any>();

  @Input() movie: Movie;
  @Input() user: User;

  foundLists: List[] = [];
  lists: List[] = [];

  selectedList: List;
  description: string;

  loadingLists = false;
  waitingForResponse = false;

  addListFormVisible = false;

  constructor(
    private cd: ChangeDetectorRef,
    private userInteractionService: UserInteractionService,
    private messageService: MessageService,
    private trackingService: TrackingService
  ) {}

  ngOnInit(): void {
    this.loadingLists = true;
    this.setUpTexts();
    this.userInteractionService
      .getLists({ username: this.user.username })
      .pipe(take(1))
      .subscribe(
        lists => {
          // todo: get lists from api
          lists.sort((a, b) => b.movies?.length - a.movies?.length);
          this.lists = lists;
          this.foundLists = lists;
          this.sortFoundListsByMoviesLenght();
          this.loadingLists = false;
          this.cd.markForCheck();
        },
        error => console.log(error)
      );
  }

  private setUpTexts() {
    this.description = $localize`:@@movie-list-handler.description:Add ${this.movie.title} to lists`;
  }

  onAddMovieToListClick(event) {
    this.waitingForResponse = true;
    // add movie to list
    this.userInteractionService
      .addMovieToList(this.movie.id, this.selectedList.slug)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: $localize`:@@movie-list-handler.add-item.success.title:Success`,
            detail: $localize`:@@movie-list-handler.add-item.success.description:Movie added to list`
          });
          this.complete.next(true);
          this.waitingForResponse = false;

          this.trackingService.trackEvent({
            action: 'AddMovieToList',
            properties: {
              category: 'Community',
              movie: this.movie.title || '',
              idMovie: this.movie.id,
              imdb: this.movie?.imdb,
              tagsMovie: getMovieTags(this.movie),
              genresMovie: getMovieGenres(this.movie),
              user: this.user.username
            }
          });
        },
        error: (/*error*/) => {
          this.messageService.add({
            severity: 'error',
            summary: $localize`:@@movie-list-handler.add-item.error.title:Error`,
            detail: $localize`:@@movie-list-handler.add-item.error.description:Movie could not be added to list`
          });

          this.waitingForResponse = false;
        }

        // eslint-disable-next-line no-restricted-syntax
      });
  }

  onCreateListClick() {
    this.addListFormVisible = true;
  }

  onListCreated(createdList: List) {
    this.addListFormVisible = false;
    this.lists = this.lists.concat(createdList);
    this.foundLists = [...this.foundLists, createdList];
    this.sortFoundListsByMoviesLenght();
    this.loadingLists = false;
    this.cd.markForCheck();
  }

  sortFoundListsByMoviesLenght() {
    this.foundLists = this.foundLists.sort((a, b) => b.movies.length - a.movies.length);
  }

  onListClick(event) {
    // make the selectedList to be the clicked list
    this.selectedList = event;
    // mark the list as selected by adding a class
    // remove the class from the other lists
  }

  search(event) {
    this.foundLists = this.lists.filter(list => list.name.toLowerCase().includes(event.query.toLowerCase()));
  }

  onAddToList(lists: List[]) {}
}
